import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { accountManagerAPI, videoAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Users,
  Search,
  Loader2,
  Film,
  Calendar,
  Eye,
  Plus,
  ArrowLeft,
  TrendingUp,
  Clock,
  Video,
  Headphones,
  MessageSquare,
  Send,
  RefreshCw,
  MoreVertical,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function MyClientsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Stats state
  const [stats, setStats] = useState({
    assigned_clients_count: 0,
    videos_created_for_clients: 0,
    videos_this_week: 0,
    videos_this_month: 0,
    total_client_videos: 0,
    active_clients_this_week: 0,
    recent_client_videos: []
  });
  
  // Client details dialog
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState("clients");
  
  // Support Tickets state
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportStats, setSupportStats] = useState(null);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportFilter, setSupportFilter] = useState({ status: "", priority: "" });
  const [supportSearch, setSupportSearch] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetailOpen, setTicketDetailOpen] = useState(false);
  const [ticketReply, setTicketReply] = useState("");
  const [ticketReplyLoading, setTicketReplyLoading] = useState(false);
  const [ticketUpdateLoading, setTicketUpdateLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const response = await accountManagerAPI.getMyClients();
      setClients(response.data.clients || []);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      if (error.response?.status === 403) {
        toast.error("You don't have account manager access");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load clients");
      }
    }
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await accountManagerAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  // Support Tickets Functions
  const fetchSupportTickets = useCallback(async () => {
    setSupportLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams();
      if (supportFilter.status) params.append("ticket_status", supportFilter.status);
      if (supportFilter.priority) params.append("priority", supportFilter.priority);
      if (supportSearch) params.append("search", supportSearch);
      params.append("limit", "50");
      
      const res = await fetch(`${BASE_URL}/api/admin/support/tickets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSupportTickets(data.tickets || []);
        setSupportStats(data.stats || null);
      }
    } catch (error) {
      console.error("Failed to fetch support tickets:", error);
    } finally {
      setSupportLoading(false);
    }
  }, [supportFilter, supportSearch]);

  const fetchTicketDetails = async (ticketId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${BASE_URL}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket(data.ticket);
      }
    } catch (error) {
      console.error("Failed to fetch ticket details:", error);
    }
  };

  const handleUpdateTicket = async (ticketId, updates) => {
    setTicketUpdateLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${BASE_URL}/api/admin/support/tickets/${ticketId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        toast.success("Ticket updated");
        fetchSupportTickets();
        if (selectedTicket?.id === ticketId) {
          fetchTicketDetails(ticketId);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to update ticket");
      }
    } catch (error) {
      toast.error("Failed to update ticket");
    } finally {
      setTicketUpdateLoading(false);
    }
  };

  const handleTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    setTicketReplyLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${BASE_URL}/api/admin/support/tickets/${selectedTicket.id}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: ticketReply })
      });
      
      if (res.ok) {
        setTicketReply("");
        fetchTicketDetails(selectedTicket.id);
        fetchSupportTickets();
        toast.success("Reply sent");
      } else {
        toast.error("Failed to send reply");
      }
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setTicketReplyLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchClients(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchClients, fetchStats]);

  // Load support tickets when tab changes to support
  useEffect(() => {
    if (activeTab === "support") {
      fetchSupportTickets();
    }
  }, [activeTab, fetchSupportTickets]);

  const handleViewClient = async (client) => {
    setSelectedClient(client);
    setClientLoading(true);
    try {
      const response = await accountManagerAPI.getClientDetails(client.id);
      setClientDetails(response.data);
    } catch (error) {
      toast.error("Failed to load client details");
    } finally {
      setClientLoading(false);
    }
  };

  const handleCreateVideoForClient = (client) => {
    // Navigate to create video page with client context
    navigate("/create", { state: { clientId: client.id, clientName: client.name, clientEmail: client.email } });
  };

  const filteredClients = clients.filter(
    (client) =>
      client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-background p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div variants={itemVariants} className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Users className="w-8 h-8 text-primary" />
                Account Manager
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage clients and handle support tickets
              </p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-secondary/30 border border-border/50">
            <TabsTrigger value="clients" className="gap-2">
              <Users className="w-4 h-4" />
              My Clients
              {stats.assigned_clients_count > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {stats.assigned_clients_count}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="support" className="gap-2" onClick={() => fetchSupportTickets()}>
              <Headphones className="w-4 h-4" />
              Support Tickets
              {supportStats?.open > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {supportStats.open}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Clients Tab Content */}
          <TabsContent value="clients" className="space-y-6 mt-6">
            {/* Stats */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.assigned_clients_count}</p>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border-cyan-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/20">
                  <Video className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-cyan-400">{stats.videos_created_for_clients}</p>
                  <p className="text-sm text-muted-foreground">Videos Created by You</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Film className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_client_videos}</p>
                  <p className="text-sm text-muted-foreground">Total Client Videos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.videos_this_week}</p>
                  <p className="text-sm text-muted-foreground">Created This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-pink-500/20">
                  <Clock className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.videos_this_month}</p>
                  <p className="text-sm text-muted-foreground">Created This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Calendar className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_clients_this_week}</p>
                  <p className="text-sm text-muted-foreground">Active This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Videos Created for Clients */}
        {stats.recent_client_videos.length > 0 && (
          <motion.div variants={itemVariants}>
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Video className="w-5 h-5 text-cyan-400" />
                  Recent Videos Created for Clients
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/50">
                  {stats.recent_client_videos.slice(0, 5).map((video) => (
                    <div key={video.id} className="flex items-center gap-4 p-4 hover:bg-secondary/30 transition-colors">
                      {video.thumbnail_url ? (
                        <img 
                          src={video.thumbnail_url} 
                          alt={video.title} 
                          className="w-16 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-16 h-10 rounded bg-secondary flex items-center justify-center">
                          <Film className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{video.title || "Untitled"}</p>
                        <p className="text-sm text-muted-foreground">
                          For: {video.client_name || video.client_email}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge 
                          variant="outline" 
                          className={
                            video.status === "completed" 
                              ? "bg-green-500/20 text-green-400 border-green-500/30" 
                              : video.status === "generating"
                              ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                              : "bg-gray-500/20 text-gray-400 border-gray-500/30"
                          }
                        >
                          {video.status}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(video.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Search */}
        <motion.div variants={itemVariants} className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-secondary/50 border-border/50"
              data-testid="client-search"
            />
          </div>
        </motion.div>

        {/* Clients Table */}
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle>Assigned Clients</CardTitle>
              <CardDescription>
                View details and create videos for your clients
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead>Client</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Videos</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {searchQuery ? "No clients match your search" : "No clients assigned yet"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredClients.map((client) => (
                      <TableRow key={client.id} className="border-border/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {client.picture ? (
                              <img
                                src={client.picture}
                                alt={client.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                <span className="text-sm font-medium">
                                  {client.name?.[0]?.toUpperCase() || "?"}
                                </span>
                              </div>
                            )}
                            <span className="font-medium">{client.name || "Unnamed"}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{client.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                            {client.video_count} videos
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(client.last_activity)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(client.assigned_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewClient(client)}
                              data-testid={`view-client-${client.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleCreateVideoForClient(client)}
                              data-testid={`create-video-${client.id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Create Video
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </motion.div>
          </TabsContent>

          {/* Support Tickets Tab Content */}
          <TabsContent value="support" className="space-y-6 mt-6">
            {/* Support Stats */}
            {supportStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-yellow-500/10 border-yellow-500/30">
                  <CardContent className="pt-4 pb-3">
                    <div className="text-2xl font-bold text-yellow-400">{supportStats.open || 0}</div>
                    <p className="text-xs text-yellow-400/80">Open</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="pt-4 pb-3">
                    <div className="text-2xl font-bold text-blue-400">{supportStats.in_progress || 0}</div>
                    <p className="text-xs text-blue-400/80">In Progress</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-500/10 border-purple-500/30">
                  <CardContent className="pt-4 pb-3">
                    <div className="text-2xl font-bold text-purple-400">{supportStats.waiting_customer || 0}</div>
                    <p className="text-xs text-purple-400/80">Waiting</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-500/10 border-green-500/30">
                  <CardContent className="pt-4 pb-3">
                    <div className="text-2xl font-bold text-green-400">{supportStats.resolved || 0}</div>
                    <p className="text-xs text-green-400/80">Resolved</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="pt-4 pb-3">
                    <div className="text-2xl font-bold text-red-400">{supportStats.urgent || 0}</div>
                    <p className="text-xs text-red-400/80">Urgent</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search tickets..."
                  value={supportSearch}
                  onChange={(e) => setSupportSearch(e.target.value)}
                  className="pl-9 bg-secondary/30 border-border/50"
                  data-testid="support-search-input"
                />
              </div>
              <Select value={supportFilter.status || "all"} onValueChange={(v) => setSupportFilter(f => ({...f, status: v === "all" ? "" : v}))}>
                <SelectTrigger className="w-36 bg-secondary/30 border-border/50">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_customer">Waiting</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={supportFilter.priority || "all"} onValueChange={(v) => setSupportFilter(f => ({...f, priority: v === "all" ? "" : v}))}>
                <SelectTrigger className="w-32 bg-secondary/30 border-border/50">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchSupportTickets()}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${supportLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* Tickets Table */}
            <Card className="bg-card border-border/50">
              <CardContent className="p-0">
                {supportLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : supportTickets.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Headphones className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No support tickets found</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50">
                        <TableHead>Ticket</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supportTickets.map((ticket) => (
                        <TableRow 
                          key={ticket.id} 
                          className="border-border/50 hover:bg-secondary/30 cursor-pointer"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setTicketDetailOpen(true);
                            fetchTicketDetails(ticket.id);
                          }}
                          data-testid={`support-ticket-row-${ticket.id}`}
                        >
                          <TableCell>
                            <div>
                              <p className="text-sm font-mono text-muted-foreground">{ticket.ticket_number}</p>
                              <p className="text-sm text-white truncate max-w-[200px]">{ticket.subject}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm text-white">{ticket.user_name || "No name"}</p>
                              <p className="text-xs text-muted-foreground">{ticket.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs capitalize">
                              {ticket.category?.replace("_", " ")}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${
                              ticket.status === "open" ? "bg-yellow-500/20 text-yellow-400" :
                              ticket.status === "in_progress" ? "bg-blue-500/20 text-blue-400" :
                              ticket.status === "waiting_customer" ? "bg-purple-500/20 text-purple-400" :
                              ticket.status === "resolved" ? "bg-green-500/20 text-green-400" :
                              "bg-gray-500/20 text-gray-400"
                            }`}>
                              {ticket.status?.replace("_", " ")}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${
                              ticket.priority === "urgent" ? "bg-red-500/20 text-red-400" :
                              ticket.priority === "high" ? "bg-orange-500/20 text-orange-400" :
                              ticket.priority === "medium" ? "bg-blue-500/20 text-blue-400" :
                              "bg-gray-500/20 text-gray-400"
                            }`}>
                              {ticket.priority}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {ticket.updated_at ? new Date(ticket.updated_at).toLocaleDateString() : "N/A"}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateTicket(ticket.id, { status: "in_progress" });
                                }}>
                                  Mark In Progress
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateTicket(ticket.id, { status: "waiting_customer" });
                                }}>
                                  Waiting on Customer
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateTicket(ticket.id, { status: "resolved" });
                                }}>
                                  <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                                  Mark Resolved
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateTicket(ticket.id, { priority: "urgent" });
                                }} className="text-red-400">
                                  Set Urgent
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Support Ticket Detail Dialog */}
      <Dialog open={ticketDetailOpen} onOpenChange={setTicketDetailOpen}>
        <DialogContent className="bg-card border-border/50 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-border/30 pb-4">
            <DialogTitle className="flex items-center gap-2 text-white">
              <Headphones className="w-5 h-5 text-primary" />
              {selectedTicket?.ticket_number}
            </DialogTitle>
            <DialogDescription className="text-lg text-white font-medium">
              {selectedTicket?.subject}
            </DialogDescription>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge className={`text-xs ${
                selectedTicket?.status === "open" ? "bg-yellow-500/20 text-yellow-400" :
                selectedTicket?.status === "in_progress" ? "bg-blue-500/20 text-blue-400" :
                selectedTicket?.status === "waiting_customer" ? "bg-purple-500/20 text-purple-400" :
                selectedTicket?.status === "resolved" ? "bg-green-500/20 text-green-400" :
                "bg-gray-500/20 text-gray-400"
              }`}>
                {selectedTicket?.status?.replace("_", " ")}
              </Badge>
              <Badge className={`text-xs ${
                selectedTicket?.priority === "urgent" ? "bg-red-500/20 text-red-400" :
                selectedTicket?.priority === "high" ? "bg-orange-500/20 text-orange-400" :
                selectedTicket?.priority === "medium" ? "bg-blue-500/20 text-blue-400" :
                "bg-gray-500/20 text-gray-400"
              }`}>
                {selectedTicket?.priority}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground mt-2">
              From: <span className="text-white">{selectedTicket?.user_name || selectedTicket?.user_email}</span>
            </div>
          </DialogHeader>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px]">
            {selectedTicket?.messages?.map((msg, idx) => (
              <div
                key={msg.id || idx}
                className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${
                  msg.sender_type === 'admin'
                    ? 'bg-primary/20 border-primary/30'
                    : 'bg-secondary/50 border-border/30'
                } border rounded-lg p-3`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-medium ${
                      msg.sender_type === 'admin' ? 'text-primary' : 'text-purple-400'
                    }`}>
                      {msg.sender_type === 'admin' ? 'Support Team' : msg.sender_name || 'User'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {msg.created_at ? new Date(msg.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                  <p className="text-sm text-white whitespace-pre-wrap">{msg.message}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Reply Input */}
          {selectedTicket?.status !== 'closed' && (
            <div className="border-t border-border/30 p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                <Select 
                  value={selectedTicket?.status || "open"} 
                  onValueChange={(v) => handleUpdateTicket(selectedTicket.id, { status: v })}
                  disabled={ticketUpdateLoading}
                >
                  <SelectTrigger className="w-40 bg-secondary/30 border-border/50">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_customer">Waiting</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select 
                  value={selectedTicket?.priority || "medium"} 
                  onValueChange={(v) => handleUpdateTicket(selectedTicket.id, { priority: v })}
                  disabled={ticketUpdateLoading}
                >
                  <SelectTrigger className="w-32 bg-secondary/30 border-border/50">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <textarea
                  placeholder="Type your reply..."
                  value={ticketReply}
                  onChange={(e) => setTicketReply(e.target.value)}
                  className="flex-1 min-h-[80px] bg-secondary/30 border border-border/50 rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleTicketReply();
                    }
                  }}
                />
                <Button
                  onClick={handleTicketReply}
                  disabled={ticketReplyLoading || !ticketReply.trim()}
                  className="self-end"
                >
                  {ticketReplyLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Details Dialog */}
      <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedClient?.picture ? (
                <img
                  src={selectedClient.picture}
                  alt={selectedClient.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-lg font-medium">
                    {selectedClient?.name?.[0]?.toUpperCase() || "?"}
                  </span>
                </div>
              )}
              {selectedClient?.name || "Client Details"}
            </DialogTitle>
          </DialogHeader>
          
          {clientLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : clientDetails ? (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{clientDetails.client?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Assigned Since</p>
                  <p className="font-medium">{formatDate(clientDetails.assigned_at)}</p>
                </div>
              </div>

              {/* Brand Kit */}
              {clientDetails.brand_kit && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Brand Kit</h3>
                  <Card className="bg-secondary/30">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {clientDetails.brand_kit.brand_name && (
                          <div>
                            <p className="text-muted-foreground">Brand Name</p>
                            <p>{clientDetails.brand_kit.brand_name}</p>
                          </div>
                        )}
                        {clientDetails.brand_kit.brand_colors?.length > 0 && (
                          <div>
                            <p className="text-muted-foreground">Brand Colors</p>
                            <div className="flex gap-1 mt-1">
                              {clientDetails.brand_kit.brand_colors.map((color, i) => (
                                <div
                                  key={i}
                                  className="w-6 h-6 rounded"
                                  style={{ backgroundColor: color }}
                                  title={color}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Recent Videos */}
              <div>
                <h3 className="text-lg font-semibold mb-2">Recent Videos ({clientDetails.videos?.length || 0})</h3>
                {clientDetails.videos?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {clientDetails.videos.slice(0, 6).map((video) => (
                      <Card key={video.id} className="bg-secondary/30">
                        <CardContent className="p-3">
                          <p className="font-medium truncate">{video.title || "Untitled"}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(video.created_at)} • {video.status}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No videos yet</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button
                  onClick={() => {
                    setSelectedClient(null);
                    handleCreateVideoForClient(selectedClient);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Video for Client
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">Failed to load client details</p>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
